import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ConfigurationService } from 'src/app/services/configuration.service';

export type EquipmentBuilderApiType = 'System' | 'Zone';

@Injectable({
  providedIn: 'root'
})
export class EquipmentBuilderService {
  constructor(
    private http: HttpClient,
    private configService: ConfigurationService
  ) { }

  private getEquipmentBuilderUrl(regionId?: string): string {
    const region = regionId || this.configService.getSelectedRegion() || '1';
    const regionConfig = this.configService.getConfig(region) || {};
    return (regionConfig.equipBuilderUrl || this.configService.getConfig('equipBuilderUrl') || '').replace(/\/$/, '');
  }

  /**
   * Fetches the sequence list for an equipment type from the equipment-builder API.
  */
  getSequences(type: EquipmentBuilderApiType, regionId?: string) {
    const params = new HttpParams().set('type', type);
    return this.http.get(`${this.getEquipmentBuilderUrl(regionId)}/equip-builder/sequences`, { params })
      .pipe(catchError(this.handleError));
  }

  /**
   * Fetches the parameter/details payload for the selected sequence.
  */
  getEquipmentDetails(type: EquipmentBuilderApiType, sequenceName: string, regionId?: string) {
    const params = new HttpParams()
      .set('type', type)
      .set('sequenceName', sequenceName);

    return this.http.get(`${this.getEquipmentBuilderUrl(regionId)}/equip-builder/equipment-details`, { params })
      .pipe(catchError(this.handleError));
  }

  /**
   * Fetches CDD catalog data for the selected equipment-builder sequence.
  */
  getCdd(sequenceName: string, regionId?: string) {
    const params = new HttpParams().set('sequenceName', sequenceName);
    return this.http.get(`${this.getEquipmentBuilderUrl(regionId)}/equip-builder/cdd`, { params })
      .pipe(catchError(this.handleError));
  }

  /**
   * Fetches the list of sequences associated with a specific workspace from the equipment-builder API.
   * @param workspaceName The name of the workspace to fetch sequences for.
   * @param regionId Optional region ID to determine the equipment builder URL.
   * @returns An observable emitting an array of sequences associated with the workspace.
   */
  getSequencesByWorkspaceName(workspaceName: string, regionId?: string): Observable<any[]> {
    const params = new HttpParams().set('workspaceName', workspaceName);
    return this.http.get<any[]>(`${this.getEquipmentBuilderUrl(regionId)}/equip-builder/sequence/findAllByWorkspaceName`, { params });
  }

  applySequence(applyRequest: any, cddFile?: File | null, regionId?: string): Observable<any> {
    // The apply endpoint expects the request JSON and optional CDD PDF as multipart parts.
    const formData = new FormData();
    formData.append(
      'applyRequest',
      new Blob([JSON.stringify(applyRequest)], { type: 'application/json' })
    );

    if (cddFile) {
      formData.append('cddFile', cddFile);
    }

    // The apply API returns the created sequence reference as plain text, not a JSON object.
    return this.http.post(`${this.getEquipmentBuilderUrl(regionId)}/equip-builder/sequence/apply`, formData, {
      responseType: 'text'
    })
      .pipe(catchError(this.handleError));
  }

  private handleError(error: any): Promise<any> {
    console.error('An error occurred', error);
    return Promise.reject(error);
  }
}
